/**
 * EventPostMortemPage — /analytics/template/event-post-mortem/:eventId
 *
 * Closed-event deep dive: YoY revenue delta + top rides + plan mix +
 * weather correlation (deferred) + breakage tile (deferred) + kiosk
 * health.
 *
 * V2.5-B Fork (2026-04-20). See docs/analytics-templates/event-post-mortem.md
 * for FLOW and docs/analytics-templates/critique-round-1.md for the
 * round-1 changes applied BEFORE this file was first authored.
 *
 * Data routing (per FLOW data-sources):
 *   - /stats (one-shot)     — event meta + metrics
 *   - /extras (one-shot)    — plan_mix + top_attractions + kiosk_throughput
 *   - /compare (404-tol.)   — YoY pct map; "no prior comparable event"
 *                             empty-state when absent
 *   - /weather (deferred)   — DeferredPane placeholder
 *   - /breakage (deferred)  — DeferredPane placeholder
 *
 * Event-still-running guard: if stats.event.status === 'live', render
 * a Banner saying "Event is still running — partial stats."
 */

import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { formatDate } from '../../lib/formatters'
import type {
  BarChart as BarChartPayload,
  DonutChart as DonutChartPayload,
} from '../../types/chart'
import { ChatChart } from '../../components/chat/ChatChart'
import {
  BannerNote,
  DeferredPane,
  TemplateAnalyticsShell,
} from '../../components/analytics/TemplateAnalyticsShell'
import { HeroKpiProgressBar } from '../../components/analytics/HeroKpiProgressBar'
import { CompactScoreTile } from '../../components/analytics/CompactScoreTile'
import { useEventAnalytics } from '../../components/analytics/useEventAnalytics'

const fmtUSD = (n: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)

function parseEventId(raw: string | undefined): number | null {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

function computeKioskHealth(
  kiosks: ReadonlyArray<{ orders: number }> | undefined | null,
): number | null {
  if (!Array.isArray(kiosks) || kiosks.length < 2) return null
  const orders = kiosks.map((k) => Math.max(0, k.orders || 0))
  const total = orders.reduce((a, b) => a + b, 0)
  if (total <= 0) return null
  const mean = total / orders.length
  if (mean <= 0) return null
  const variance =
    orders.reduce((acc, v) => acc + (v - mean) ** 2, 0) / orders.length
  const stdev = Math.sqrt(variance)
  const cv = stdev / mean
  return Math.round(Math.max(0, 1 - cv) * 100)
}

function captionForScore(pct: number | null): string | undefined {
  if (pct === null) return undefined
  if (pct >= 85) return 'Excellent'
  if (pct >= 70) return 'Above average'
  if (pct >= 55) return 'Healthy'
  if (pct >= 35) return 'Below average'
  return 'Needs attention'
}

export function EventPostMortemPage() {
  const { setCurrentEventId } = useAuth()
  const params = useParams<{ eventId?: string }>()
  const eventId = parseEventId(params.eventId)

  useEffect(() => {
    if (eventId) setCurrentEventId(eventId)
  }, [eventId, setCurrentEventId])

  const {
    stats,
    statsLoading,
    statsError,
    extras,
    extrasAvailable,
    yoy,
  } = useEventAnalytics({ eventId })

  const topAttractionsChart = useMemo<BarChartPayload | null>(() => {
    if (!extras?.top_attractions?.length) return null
    return {
      type: 'bar',
      title: 'Top rides by scans',
      orientation: 'horizontal',
      series: extras.top_attractions.slice(0, 5).map((a) => ({
        label: a.attraction_name,
        value: a.scans,
      })),
      xLabel: 'Scans',
    }
  }, [extras])

  const planMixChart = useMemo<DonutChartPayload | null>(() => {
    if (!extras?.plan_mix?.length) return null
    const totalOrders = extras.plan_mix.reduce(
      (sum, p) => sum + (p.orders || 0),
      0,
    )
    return {
      type: 'donut',
      title: 'Plan mix',
      slices: extras.plan_mix.map((p) => ({
        label: p.plan_name,
        value: p.orders,
      })),
      centerLabel: totalOrders
        ? `${new Intl.NumberFormat('en-US').format(totalOrders)} orders`
        : undefined,
    }
  }, [extras])

  const kioskHealth = useMemo(
    () => computeKioskHealth(extras?.kiosk_throughput),
    [extras],
  )

  const isStillRunning = stats?.event.status === 'live'
  const hasYoy = yoy && typeof yoy.revenue_total === 'number'

  // Empty — no event in URL.
  if (eventId == null) {
    return (
      <TemplateAnalyticsShell title="Event post-mortem">
        <div
          data-testid="postmortem-empty"
          className="rounded-card border border-dashed border-hairline bg-surface-stripe p-8 text-center text-sm text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
        >
          Pick an event to see its post-mortem — revenue comparison, top
          rides, plan mix, and breakage.
        </div>
      </TemplateAnalyticsShell>
    )
  }

  // Hard error — /stats failed and we have nothing to fall back on.
  if (statsError && !stats) {
    return (
      <TemplateAnalyticsShell title="Event post-mortem" eventName={null}>
        <BannerNote tone="warning" data-testid="postmortem-stats-error">
          Couldn&apos;t load event stats: {statsError}
        </BannerNote>
      </TemplateAnalyticsShell>
    )
  }

  return (
    <TemplateAnalyticsShell
      title="Event post-mortem"
      subtitle={
        stats
          ? `${stats.event.event_name} · ${formatDate(stats.event.event_date)}`
          : 'Loading event…'
      }
      eventName={stats?.event.event_name ?? null}
      windowChip={stats ? formatDate(stats.event.event_date) : null}
      banner={
        isStillRunning ? (
          <BannerNote data-testid="postmortem-live-banner">
            Event is still running — stats below reflect partial sales.
          </BannerNote>
        ) : null
      }
    >
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        data-testid="postmortem-grid"
      >
        {/* 1. YoY Revenue hero */}
        <HeroKpiProgressBar
          metric="revenue"
          value={stats?.metrics.revenue_total ?? null}
          capacity={null}
          yoyPct={yoy?.revenue_total ?? null}
          formatValue={fmtUSD}
          loading={statsLoading && !stats}
          label="YoY revenue"
          data-testid="postmortem-hero-revenue"
        />

        {/* No-prior-comparable-event state — the headline promise of this
            page. Distinct from loading + silent-absence. */}
        {!hasYoy && !statsLoading ? (
          <section
            data-testid="postmortem-no-prior"
            className="rounded-card border border-dashed border-hairline bg-surface-stripe p-4 text-xs text-ink-soft dark:border-slate-700 dark:bg-slate-800/60"
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-butler-700 dark:text-butler-200">
              Year-over-year
            </div>
            <p className="mt-2">No prior comparable event found for YoY.</p>
          </section>
        ) : null}

        {/* 2. Top rides */}
        {topAttractionsChart ? (
          <ChatChart chart={topAttractionsChart} density="canvas" />
        ) : (
          <DeferredPane
            title="Top rides"
            message={
              extrasAvailable
                ? 'No ride scans logged for this event.'
                : 'Attraction rollup isn\u2019t available for this event.'
            }
          />
        )}

        {/* 3. Plan mix donut */}
        {planMixChart ? (
          <ChatChart chart={planMixChart} density="canvas" />
        ) : (
          <DeferredPane
            title="Plan mix"
            message={
              extrasAvailable
                ? 'No plan sales logged for this event.'
                : 'Plan rollup isn\u2019t available for this event.'
            }
          />
        )}

        {/* 4. Weather (deferred) */}
        <DeferredPane
          title="Weather correlation"
          message="Weather data isn't connected for this event yet."
        />

        {/* 5. Breakage (deferred) */}
        <DeferredPane
          title="Breakage summary"
          message="Breakage summary isn't available for this event."
        />

        {/* 6. Kiosk health */}
        <CompactScoreTile
          label="Kiosk health"
          scorePct={extrasAvailable ? kioskHealth : null}
          caption={captionForScore(extrasAvailable ? kioskHealth : null)}
          unavailableMessage={
            extrasAvailable
              ? 'Need 2+ kiosks for a health score'
              : 'Kiosk data unavailable'
          }
          data-testid="postmortem-kiosk-health"
        />
      </div>
    </TemplateAnalyticsShell>
  )
}
